/**
 * @fileoverview Connect source-version identity to explicit AI edit proposals and staff adoption. Backend capability and run records govern available actions; the panel owns instructions, polling, and proposal review. `kc-internal-backend/functions/document_ai.py` owns isolated draft work and managed adoption. A generated proposal and an accepted canonical version are separate state; the selected source identity must survive refresh and async completion.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import api from '../../api'
import AppIcon from '../shared/AppIcon'
import { apiUrl, versionLabel } from './archiveReviewUtils'
import './AiDocumentEditPanel.css'

const POLLABLE = new Set(['queued', 'preparing', 'generating', 'applying', 'proposal_ready', 'building_redline'])
const UNRESOLVED = new Set([...POLLABLE, 'ready'])
const ENHANCING_COMPARISON = new Set(['queued', 'running'])
const PHASES = {
    queued: ['pi pi-clock', 'Queued'],
    preparing: ['pi pi-file', 'Reading document'],
    generating: ['pi pi-sparkles', 'Drafting edits'],
    applying: ['pi pi-pencil', 'Building proposal'],
    proposal_ready: ['pi pi-check-circle', 'Proposal ready'],
    building_redline: ['pi pi-spin pi-spinner', 'Building Word redline'],
    ready: ['pi pi-check-circle', 'Ready to accept'],
    failed: ['pi pi-exclamation-circle', 'Could not propose edits'],
    no_changes: ['pi pi-check-circle', 'No changes needed'],
    cancelled: ['pi pi-times-circle', 'Cancelled'],
    discarded: ['pi pi-trash', 'Discarded'],
    adopted: ['pi pi-check', 'Accepted'],
    stale: ['pi pi-exclamation-triangle', 'Document changed'],
}



const runActivity = (run) => {
    const events = Array.isArray(run?.progress) ? run.progress : []
    return [...events].reverse().find((item) => item?.status === 'active') || events[events.length - 1] || null
}



const runLabel = (run) => runActivity(run)?.label || (PHASES[run?.status] || [null, run?.status])[1]



const formatElapsed = (seconds) => {
    const total = Math.max(0, Number(seconds || 0))
    if (total < 60) return `${total}s`
    return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`
}



function AiDocumentEditPanel({
    documentId,
    sourceVersion,
    versions = [],
    onAdopted,
    onReviewChange,
    onAvailabilityChange,
    sidebar = false,
    expanded = false,
    onExpand,
}) {
    const ownerEditId = useMemo(() => {
        const token = window.crypto?.randomUUID?.()
            || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
        return `owner-edit-${token}`
    }, [documentId])
    const [capability, setCapability] = useState(null)
    const [instruction, setInstruction] = useState('')
    const [templateId, setTemplateId] = useState('')
    const [sourceRevisionId, setSourceRevisionId] = useState(sourceVersion?.revision_id || '')
    const [run, setRun] = useState(null)
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [action, setAction] = useState('')
    const [error, setError] = useState('')
    const pollCount = useRef(0)
    const reviewChangeRef = useRef(onReviewChange)
    const availabilityChangeRef = useRef(onAvailabilityChange)

    useEffect(() => {
        reviewChangeRef.current = onReviewChange
    }, [onReviewChange])

    useEffect(() => {
        availabilityChangeRef.current = onAvailabilityChange
    }, [onAvailabilityChange])

    useEffect(

    () => {
        let active = true
        setCapability(null)
        setRun(null)
        setInstruction('')
        setTemplateId('')
        setSourceRevisionId(sourceVersion?.revision_id || '')
        setError('')
        setLoading(true)
        availabilityChangeRef.current?.(false)
        pollCount.current = 0
        if (!documentId) return () => { active = false }
        const base = `/simul/archive/documents/${encodeURIComponent(documentId)}/ai-edits`
        api.get(`${base}/capability`)
            .then(

            async (response) => {
                if (!active) return
                const nextCapability = response.data?.capability || null
                setCapability(nextCapability)
                availabilityChangeRef.current?.(Boolean(nextCapability?.available))
                if (!nextCapability?.available) return
                const runsResponse = await api.get(base, { params: { limit: 5 } })
                if (!active) return
                const recent = runsResponse.data?.runs || []
                setRun(recent.find((item) => UNRESOLVED.has(item.status)) || null)
            })
            .catch((requestError) => {
                if (!active) return
                availabilityChangeRef.current?.(false)
                setError(requestError.response?.data?.error || 'AI editing is unavailable.')
            })
            .finally(() => { if (active) setLoading(false) })
        return () => { active = false }
    }, [documentId, sourceVersion?.revision_id])

    const sourceVersions = useMemo(

    () => {
        const candidates = versions.length ? versions : sourceVersion ? [sourceVersion] : []
        const unique = new Map()
        candidates.forEach((version) => {
            if (version?.revision_id && version.provenance !== 'ai_edit_proposal') {
                unique.set(version.revision_id, version)
            }
        })
        return [...unique.values()].sort((left, right) => {
            const indexDifference = Number(right.version_index || 0) - Number(left.version_index || 0)
            if (indexDifference) return indexDifference
            return String(right.created_at || '').localeCompare(String(left.created_at || ''))
        })
    }, [sourceVersion, versions])
    const selectedSourceVersion = sourceVersions.find(
        (version) => version.revision_id === sourceRevisionId,
    ) || sourceVersion || sourceVersions[0] || null

    useEffect(

    () => {
        const enhancingComparison = ENHANCING_COMPARISON.has(run?.comparison?.changed_pages_status)
        if (
            !documentId ||
            !run?.run_id ||
            (!POLLABLE.has(run.status) && !enhancingComparison) ||
            run.comparison?.status === 'failed'
        ) return undefined
        let active = true
        const delay = pollCount.current < 20 ? 500 : 1000
        const timer = window.setTimeout(

        async () => {
            try {
                const response = await api.get(`/simul/archive/documents/${encodeURIComponent(documentId)}/ai-edits/${encodeURIComponent(run.run_id)}`)
                if (active) {
                    pollCount.current += 1
                    setRun(response.data?.run || null)
                }
            } catch (requestError) {
                if (active) setError(requestError.response?.data?.error || 'Proposal status could not be refreshed.')
            }
        }, delay)
        return () => {
            active = false
            window.clearTimeout(timer)
        }
    }, [documentId, run])

    const busy = Boolean(run && UNRESOLVED.has(run.status))
    const remaining = Number(capability?.max_instruction_characters || 12000) - instruction.length
    const send =

    async () => {
        const text = instruction.trim()
        if (!text || !selectedSourceVersion?.revision_id || submitting) return
        setSubmitting(true)
        setError('')
        pollCount.current = 0
        reviewChangeRef.current?.(null)
        try {
            const payload = {
                instruction: text,
                source_revision_id: selectedSourceVersion.revision_id,
                source_content_sha256: selectedSourceVersion.content_sha256,
                owner_edit_id: ownerEditId,
            }
            if (templateId) payload.template_id = templateId
            const response = await api.post(`/simul/archive/documents/${encodeURIComponent(documentId)}/ai-edits`, payload)
            setRun(response.data?.run || null)
        } catch (requestError) {
            setError(requestError.response?.data?.error || 'AI edit request could not start.')
        } finally {
            setSubmitting(false)
        }
    }

    const runAction = useCallback(

    async (type) => {
        if (!run?.run_id || action) return
        setAction(type)
        setError('')
        try {
            const response = await api.post(
                `/simul/archive/documents/${encodeURIComponent(documentId)}/ai-edits/${encodeURIComponent(run.run_id)}/${type}`,
                type === 'adopt' ? { confirm: true } : {},
            )
            const nextRun = response.data?.run || null
            setRun(nextRun)
            if (type === 'adopt') {
                setInstruction('')
                reviewChangeRef.current?.(null)
                await onAdopted?.(response.data?.version?.revision_id || '')
            } else if (type === 'discard') {
                reviewChangeRef.current?.(null)
            }
        } catch (requestError) {
            const fallback = {
                adopt: 'Proposal could not be adopted.',
                cancel: 'Proposal could not be cancelled.',
                discard: 'Proposal could not be discarded.',
            }[type] || 'Proposal action failed.'
            setError(requestError.response?.data?.error || fallback)
        } finally {
            setAction('')
        }
    }, [action, documentId, onAdopted, run])

    useEffect(() => {
        const reviewable = ['proposal_ready', 'building_redline', 'ready'].includes(run?.status)
        const resolved = ['adopted', 'discarded', 'cancelled', 'failed', 'stale', 'no_changes'].includes(run?.status)
        if (reviewable && run?.comparison?.comparison_id) {
            reviewChangeRef.current?.({
                run,
                action,
                accept: () => runAction('adopt'),
                reject: () => runAction('discard'),
            })
        } else if (!run || resolved) {
            reviewChangeRef.current?.(null)
        }
    }, [action, run, runAction])

    if (loading || !capability?.available) return null

    const content = (
        <>
            {!busy ? (
                <>
                    {capability.template_mode ? (
                        <div className="documents-ai-edit__template-guard" role="status">
                            <AppIcon icon="pi pi-shield" aria-hidden="true" />
                            <span>Template expressions are protected</span>
                        </div>
                    ) : null}
                    <div className="documents-ai-edit__composer">
                        <label className="documents-ai-edit__instruction">
                            <span>Editing instructions</span>
                            <textarea
                                value={instruction}
                                onChange={(event) => setInstruction(event.target.value)}
                                onKeyDown={(event) => {
                                    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                                        event.preventDefault()
                                        send()
                                    }
                                }}
                                maxLength={capability.max_instruction_characters || 12000}
                                rows={4}
                                disabled={submitting}
                                aria-label="Describe edits to this document"
                                placeholder="Describe the changes. Reference any client, case, defendant, or matter in your own words…"
                            />
                        </label>
                    </div>
                    <div className={`documents-ai-edit__context ${(capability.templates || []).length ? '' : 'is-template-only'}`} aria-label="Editing options">
                        <label title="Choose the document version the proposal starts from">
                            <span>Starting point</span>
                            <select
                                value={selectedSourceVersion?.revision_id || ''}
                                onChange={(event) => setSourceRevisionId(event.target.value)}
                                aria-label="Starting version for AI edits"
                                disabled={submitting}>
                                {sourceVersions.map((version) => (
                                    <option key={version.revision_id} value={version.revision_id}>
                                        {versionLabel(version)}{version.is_latest ? ' (Latest)' : ''}
                                    </option>
                                ))}
                            </select>
                        </label>
                        {(capability.templates || []).length ? (
                            <label title="Optionally apply an approved template">
                                <span>Template</span>
                                <select
                                    value={templateId}
                                    onChange={(event) => setTemplateId(event.target.value)}
                                    aria-label="Template for AI edits"
                                    disabled={submitting}>
                                    <option value="">No template</option>
                                    {(capability.templates || []).map((template) => (
                                        <option key={template.template_id} value={template.template_id}>{template.title}</option>
                                    ))}
                                </select>
                            </label>
                        ) : null}
                    </div>
                    <div className="documents-ai-edit__submit">
                        <span>⌘ Enter</span>
                        <button
                            type="button"
                            className="documents-ai-edit__send"
                            onClick={send}
                            disabled={!instruction.trim() || !selectedSourceVersion?.revision_id || remaining < 0 || submitting}
                            aria-label="Propose document edits">
                            <AppIcon icon={submitting ? 'pi pi-spin pi-spinner' : 'pi pi-file-edit'} aria-hidden="true" />
                            {submitting ? 'Preparing edits…' : 'Propose edits'}
                        </button>
                    </div>
                </>
            ) : null}

            {error ? <div className="documents-ai-edit__error" role="alert"><AppIcon icon="pi pi-exclamation-circle" /> {error}</div> : null}
            {run ? <AiRun run={run} action={action} onAction={runAction} /> : null}
        </>
    )

    if (sidebar) {
        const phaseLabel = runLabel(run)
        return (
            <section
                className={`documents-ai-edit is-sidebar documents-sidebar-accordion-section ${expanded ? 'is-expanded' : ''}`}
                aria-label="AI document editing">
                <button
                    type="button"
                    className="documents-sidebar-accordion-trigger"
                    aria-label="AI editing"
                    aria-expanded={expanded}
                    aria-controls="documents-sidebar-ai"
                    onClick={onExpand}>
                    <div className="documents-archive-section-title">
                        <h2>AI editing</h2>
                        <span>{phaseLabel || 'Propose document changes'}</span>
                    </div>
                    <AppIcon icon={expanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'} aria-hidden="true" />
                </button>
                <div id="documents-sidebar-ai" className="documents-ai-edit__sidebar-content" hidden={!expanded}>
                    {content}
                </div>
            </section>
        )
    }

    return (
        <section className={`documents-ai-edit ${run ? 'has-run' : ''}`} aria-label="Suggest edits with AI">
            {content}
        </section>
    )
}



function AiRun({ run, action, onAction }) {
    const [icon, phaseLabel] = PHASES[run.status] || ['pi pi-clock', run.status]
    const label = runLabel(run) || phaseLabel
    const activity = (Array.isArray(run.progress) ? run.progress : []).slice(-5)
    const comparisonReady = run.comparison?.status === 'ready'
    const comparisonFailed = run.comparison?.status === 'failed'
    const progress = ['queued', 'preparing', 'generating', 'applying'].includes(run.status)
    const reviewable = ['proposal_ready', 'building_redline', 'ready'].includes(run.status)
    const resolved = ['adopted', 'discarded', 'cancelled', 'failed', 'stale', 'no_changes'].includes(run.status)
    const proposalUrl = run.proposal_revision_id
        ? apiUrl(`/simul/archive/versions/${encodeURIComponent(run.proposal_revision_id)}/download`)
        : ''

    return (
        <div className={`documents-ai-edit__run is-${run.status}`}>
            <header aria-live="polite">
                <span className="documents-ai-edit__phase"><AppIcon icon={icon} aria-hidden="true" /> {label}</span>
                {run.matter_number ? <span className="documents-ai-edit__source"><AppIcon icon="pi pi-briefcase" /> {run.matter_number}</span> : null}
                {run.template?.title ? <span className="documents-ai-edit__source"><AppIcon icon="pi pi-copy" /> {run.template.title}</span> : null}
                {progress ? (
                    <time aria-label={`Elapsed time ${formatElapsed(run.elapsed_seconds)}`}>{formatElapsed(run.elapsed_seconds)}</time>
                ) : run.stage_ms?.total_to_proposal ? (
                    <time>{(run.stage_ms.total_to_proposal / 1000).toFixed(1)}s</time>
                ) : null}
                {progress && run.can_cancel ? (
                    <button type="button" onClick={() => onAction('cancel')} disabled={Boolean(action)}>Cancel</button>
                ) : null}
            </header>
            {progress ? <div className="documents-ai-edit__progress"><span /></div> : null}
            {progress && activity.length ? (
                <ol className="documents-ai-edit__activity" aria-label="Agent activity" aria-live="polite">
                    {activity.map((item, index) => {
                        const itemStatus = ['active', 'complete', 'failed'].includes(item?.status)
                            ? item.status
                            : 'complete'
                        const itemIcon = {
                            active: 'pi pi-spin pi-spinner',
                            complete: 'pi pi-check',
                            failed: 'pi pi-exclamation-circle',
                        }[itemStatus]
                        return (
                            <li className={`is-${itemStatus}`} key={`${item?.key || item?.label || 'activity'}-${index}`}>
                                <AppIcon icon={itemIcon} aria-hidden="true" />
                                <span>{item?.label || 'Working on edits'}</span>
                                {item?.detail ? <small>{item.detail}</small> : null}
                            </li>
                        )
                    })}
                </ol>
            ) : null}
            {run.error ? <div className="documents-ai-edit__error" role="alert">{run.error}</div> : null}
            {run.summary ? <p className="documents-ai-edit__summary">{run.summary}</p> : null}
            {run.warnings?.length ? (
                <ul className="documents-ai-edit__warnings">
                    {run.warnings.map((warning, index) => <li key={`${warning}-${index}`}>{warning}</li>)}
                </ul>
            ) : null}
            {reviewable ? (
                <footer aria-label="AI change review">
                    <div>
                        {comparisonReady ? (
                            <span className="is-reviewing"><AppIcon icon="pi pi-file-edit" /> Review changes in the document</span>
                        ) : comparisonFailed ? (
                            <span className="is-failed"><AppIcon icon="pi pi-exclamation-triangle" /> Redline failed</span>
                        ) : (
                            <span><AppIcon icon="pi pi-spin pi-spinner" /> Redline rendering</span>
                        )}
                        {proposalUrl ? <a href={proposalUrl}><AppIcon icon="pi pi-download" /> DOCX</a> : null}
                    </div>
                    <button type="button" className="is-discard" onClick={() => onAction('discard')} disabled={Boolean(action)}>
                        <AppIcon icon="pi pi-times" aria-hidden="true" />
                        {action === 'discard' ? 'Rejecting…' : 'Reject proposal'}
                    </button>
                    <button type="button" className="is-adopt" onClick={() => onAction('adopt')} disabled={!run.can_adopt || Boolean(action)} title={run.can_adopt ? 'Accept edits and save an AI checkpoint' : 'Proposal is not ready yet'}>
                        <AppIcon icon={action === 'adopt' ? 'pi pi-spin pi-spinner' : 'pi pi-check'} />
                        {action === 'adopt' ? 'Saving…' : 'Accept edits'}
                    </button>
                </footer>
            ) : null}
            {resolved && run.status !== 'failed' ? <div className="documents-ai-edit__resolved"><AppIcon icon={icon} /> {phaseLabel}</div> : null}
        </div>
    )
}

export default AiDocumentEditPanel
